const OCR_PERCENT_START = 5;
const OCR_PERCENT_END = 22;
const SUPPLEMENT_PERCENT_START = 22;
const SUPPLEMENT_PERCENT_END = 28;
const EXTRACTION_PERCENT_START = 28;
const EXTRACTION_PERCENT_END = 45;
const COMPLIANCE_PERCENT_START = 45;
const COMPLIANCE_PERCENT_END = 92;
/**
 * Map page/section progress to a visible % when weighted overall_pct moves too slowly.
 */
export function resolveDisplayPercent(payload, prevPercent = 0, { cap = 99 } = {}) {
  if (!payload) return Math.max(0, prevPercent);
  const overallRaw = payload.overall_pct ?? payload.overallPct;
  const overallNum = overallRaw != null && overallRaw !== '' ? Number(overallRaw) : null;
  const phase = String(payload.phase || '').toLowerCase();
  const progress = Number(payload.progress);
  const total = Number(payload.total);
  let computed = Number.isFinite(overallNum) ? overallNum : null;
  if (phase === 'ocr' && Number.isFinite(progress) && Number.isFinite(total) && total > 0) {
    const fromPages = OCR_PERCENT_START
      + (progress / total) * (OCR_PERCENT_END - OCR_PERCENT_START);
    computed = computed != null ? Math.max(computed, fromPages) : fromPages;
  } else if (
    phase === 'supplement'
    && Number.isFinite(progress)
    && Number.isFinite(total)
    && total > 0
  ) {
    const fromPages = SUPPLEMENT_PERCENT_START
      + (progress / total) * (SUPPLEMENT_PERCENT_END - SUPPLEMENT_PERCENT_START);
    computed = computed != null ? Math.max(computed, fromPages) : fromPages;
  } else if (
    phase === 'extraction'
    && Number.isFinite(progress)
    && Number.isFinite(total)
    && total > 0
  ) {
    const fromParts = EXTRACTION_PERCENT_START
      + (progress / total) * (EXTRACTION_PERCENT_END - EXTRACTION_PERCENT_START);
    computed = computed != null ? Math.max(computed, fromParts) : fromParts;
  } else if (phase === 'prep') {
    const prepPct = EXTRACTION_PERCENT_END - 2;
    computed = computed != null ? Math.max(computed, prepPct) : prepPct;
  } else if (phase === 'embedding') {
    const embedPct = EXTRACTION_PERCENT_END;
    computed = computed != null ? Math.max(computed, embedPct) : embedPct;
  } else if (
    phase === 'compliance'
    && Number.isFinite(progress)
    && Number.isFinite(total)
    && total > 0
  ) {
    // Multi-IEP bundles process `total` sections once per cycle. Anchor on the
    // active cycle so the bar reflects global progress, not per-cycle (defaults
    // to single-cycle behavior when cycle_total is absent).
    const cycleTotal = Math.max(1, Number(payload.cycle_total) || 1);
    const cycleIndex = Math.min(cycleTotal, Math.max(1, Number(payload.cycle_index) || 1));
    const globalDone = (cycleIndex - 1) * total + progress;
    const globalTotal = cycleTotal * total;
    const fromSections = COMPLIANCE_PERCENT_START
      + (globalDone / globalTotal) * (COMPLIANCE_PERCENT_END - COMPLIANCE_PERCENT_START);
    computed = computed != null ? Math.max(computed, fromSections) : fromSections;
  }
  if (computed == null || !Number.isFinite(computed)) {
    return Math.max(0, prevPercent);
  }
  return Math.min(cap, Math.max(prevPercent, computed));
}

/** True when the job is waiting for a GPU slot — not actively processing. */
export function isJobQueued(payload) {
  if (!payload) return false;
  const event = String(payload.event || '');
  const phase = String(payload.phase || '').toLowerCase();
  const status = String(payload.status || '').toLowerCase();
  if (event === 'job_progress' || event === 'job_started' || event === 'pipeline_complete') {
    return false;
  }
  if (['ocr', 'supplement', 'extraction', 'prep', 'embedding', 'compliance', 'running', 'finalize', 'saving'].includes(phase)) {
    return false;
  }
  if (status === 'running' || status === 'saving') {
    return false;
  }
  return event === 'job_queued'
    || event === 'job_queue_position'
    || (event === 'job_estimated' && phase === 'queued')
    || (status === 'queued' && (phase === 'queued' || !phase));
}

const TERMINAL_JOB_STATUSES = new Set(['completed', 'failed', 'cancelled']);

/** Drop jobs that are no longer running (Redis or tracking can lag behind activity). */
export function filterActiveJobs(jobs) {
  return (jobs || []).filter((job) => {
    const status = String(job.status || '').toLowerCase();
    if (TERMINAL_JOB_STATUSES.has(status)) return false;
    if (job.error || job.completed_at || job.completedAt) return false;
    const event = String(job.event || '').toLowerCase();
    return event !== 'job_failed';
  });
}

/** Redis stores list fields as JSON strings; SSE snapshots must still be arrays. */
export function parseJobCycles(raw) {
  if (!raw) return null;
  let rows = raw;
  if (typeof rows === 'string') {
    const s = rows.trim();
    if (!s || s === 'null' || s === 'undefined') return null;
    try {
      rows = JSON.parse(s);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(rows)) return null;
  const out = rows.filter((row) => row && typeof row === 'object' && row.anchor);
  return out.length ? out : null;
}

/** If compliance is underway but Redis still has every cycle queued, treat the first as 1/N. */
export function withLiveCycleProgress(cycles, job = {}) {
  const rows = (parseJobCycles(cycles) || []).map((c, i) => ({
    ...c,
    index: c.index || i + 1,
  }));
  if (!rows.length) return [];
  const phase = String(job.phase || '').toLowerCase();
  const msg = String(job.message || job.phaseLabel || '');
  const pct = Number(job.percent ?? job.overall_pct ?? job.overallPct);
  const inCompliance = ['compliance', 'running', 'finalize', 'saving'].includes(phase)
    || /checking compliance|scoring the current|found\s+\d+\s+iep cycle/i.test(msg)
    || (Number.isFinite(pct) && pct > 45 && rows.length > 1);
  const hasActive = rows.some((c) => c.status === 'running' || c.status === 'done');
  if (hasActive || !inCompliance) return rows;
  const done = Number(job.progress);
  const total = Number(job.total) || Number(job.sections_total) || 12;
  return rows.map((c, i) => {
    if (i !== 0) return c;
    const remaining = Number(c.remaining_seconds);
    const estimated = Number(c.estimated_seconds);
    return {
      ...c,
      status: 'running',
      sections_done: Number.isFinite(done) ? done : (c.sections_done || 0),
      sections_total: Number.isFinite(total) && total > 0 ? total : (c.sections_total || 12),
      remaining_seconds: Number.isFinite(remaining)
        ? remaining
        : (Number.isFinite(estimated) ? estimated : undefined),
      seconds: c.seconds || 0,
    };
  });
}

export function cycleYearLabel(anchor) {
  const raw = String(anchor || '');
  const m = raw.match(/(\d{4})/);
  if (m) return `${m[1]} IEP`;
  const y = new Date(raw).getFullYear();
  return Number.isFinite(y) ? `${y} IEP` : null;
}

export function rewriteFoundCyclesLabel(message, cycleIndex, cycleTotal) {
  const raw = String(message || '').trim();
  const found = raw.match(/found\s+(\d+)\s+iep\s+cycles/i);
  const n = Number(cycleTotal) || (found ? Number(found[1]) : 0);
  if (n > 1 && (found || /scoring the current/i.test(raw))) {
    return `Checking compliance — ${Number(cycleIndex) || 1}/${n}`;
  }
  return raw;
}

/** 1/N pointer for the float + pipeline even when Redis never set cycle_anchor. */
export function deriveCyclePointer(job = {}) {
  const rows = withLiveCycleProgress(job.cycles, job);
  const runningIdx = rows.findIndex((c) => c.status === 'running');
  const doneCount = rows.filter((c) => c.status === 'done').length;
  let cycleTotal = Number(job.cycle_total ?? job.cycleTotal);
  if (!Number.isFinite(cycleTotal) || cycleTotal < 2) {
    cycleTotal = rows.length > 1 ? rows.length : 0;
  }
  let cycleIndex = Number(job.cycle_index ?? job.cycleIndex);
  if (!Number.isFinite(cycleIndex) || cycleIndex < 1) {
    if (runningIdx >= 0) cycleIndex = runningIdx + 1;
    else if (cycleTotal > 1) cycleIndex = Math.min(doneCount + 1, cycleTotal);
    else cycleIndex = null;
  }
  const cycleAnchor = job.cycle_anchor || job.cycleAnchor
    || (runningIdx >= 0 ? rows[runningIdx]?.anchor : rows[0]?.anchor)
    || null;
  return {
    cycles: rows,
    cycleIndex: cycleTotal > 1 ? cycleIndex : null,
    cycleTotal: cycleTotal > 1 ? cycleTotal : 0,
    cycleAnchor,
    cycleLabel: cycleYearLabel(cycleAnchor),
    phaseLabel: rewriteFoundCyclesLabel(job.phaseLabel || job.message, cycleIndex, cycleTotal),
  };
}

/** Normalize Redis job hash (string fields) for progress UI / SSE handlers. */
export function normalizeJobSnapshot(job) {
  if (!job || (!job.job_id && !job.jobId)) return null;
  const num = (v) => {
    if (v == null || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const status = String(job.status || '').toLowerCase();
  if (status === 'completed') {
    return {
      event: 'job_completed',
      job_id: job.job_id || job.jobId,
      job_type: job.job_type || job.jobType,
      overall_pct: 100,
      remaining_seconds: 0,
      results_ready: job.results_ready ?? job.resultsReady ?? true,
      status,
    };
  }
  if (status === 'failed' || status === 'cancelled') {
    return {
      event: 'job_failed',
      job_id: job.job_id || job.jobId,
      job_type: job.job_type || job.jobType,
      error: job.error || job.message,
      status,
    };
  }
  const phase = String(job.phase || '').toLowerCase();
  const rawEvent = job.event || '';
  let event = rawEvent;
  if (!event) {
    if (status === 'saving' || phase === 'saving') event = 'pipeline_complete';
    else if (
      phase === 'ocr'
      || phase === 'supplement'
      || phase === 'extraction'
      || phase === 'prep'
      || phase === 'embedding'
      || phase === 'compliance'
      || phase === 'running'
      || phase === 'finalize'
    ) {
      event = 'job_progress';
    } else if (status === 'queued' && phase === 'queued') event = 'job_queue_position';
    else if (status === 'running' || status === 'processing') event = 'job_progress';
    else if (status === 'queued') event = 'job_queue_position';
    else event = 'job_progress';
  } else if (
    (event === 'job_queue_position' || event === 'job_queued')
    && (
      phase === 'ocr'
      || phase === 'supplement'
      || phase === 'extraction'
      || phase === 'prep'
      || phase === 'embedding'
      || phase === 'compliance'
      || phase === 'running'
      || phase === 'finalize'
    )
  ) {
    event = 'job_progress';
  }
  const snapshot = {
    event,
    job_id: job.job_id || job.jobId,
    job_type: job.job_type || job.jobType,
    overall_pct: num(job.overall_pct ?? job.overallPct),
    remaining_seconds: num(job.remaining_seconds ?? job.remainingSeconds),
    estimated_seconds: num(job.estimated_seconds ?? job.estimatedSeconds),
    message: job.message || undefined,
    phase: job.phase,
    queue_position: num(job.queue_position ?? job.queuePosition),
    results_ready: job.results_ready ?? job.resultsReady,
    section: job.section || job.section_name,
    section_label: job.section_label || job.sectionLabel,
    progress: num(job.progress),
    total: num(job.total),
    status,
    cycle_anchor: job.cycle_anchor || job.cycleAnchor || undefined,
    cycle_index: num(job.cycle_index ?? job.cycleIndex),
    cycle_total: num(job.cycle_total ?? job.cycleTotal),
    cycles: parseJobCycles(job.cycles) || undefined,
    compliance_id: job.compliance_id ?? job.complianceId ?? undefined,
  };
  if (isJobQueued(snapshot)) {
    snapshot.status = 'queued';
  } else if (status === 'queued' && event === 'job_progress') {
    snapshot.status = 'running';
  }
  return snapshot;
}

export function pickLatestJob(jobs) {
  if (!jobs?.length) return null;
  return [...jobs].sort((a, b) => {
    const ta = Date.parse(a.updated_at || a.ts || 0) || 0;
    const tb = Date.parse(b.updated_at || b.ts || 0) || 0;
    return tb - ta;
  })[0];
}